import type { SavedViewConfig, SortDirection } from "@/components/shared/UniversalCRMTable/types";
import { CONTACT_FIELDS, type ContactFieldId } from "./contact-field-ids";
import { collectFieldsForSavedView } from "./contact-saved-view-helpers";
import {
  useContactsWithNoTicketsThisMonth,
  useContactsWithTicketsThisMonth,
  useContactsWithNoTicketsLastMonth,
  useContactsWithTicketsLastMonth,
} from "@/features/contacts/hooks/contacts-ticket-filter-hooks";

/**
 * Returns the visible-column list for `viewId`, derived from each contact
 * field's `savedViewMembership` declaration. `order` lists the historical
 * left-to-right column ordering for views whose order differs from field
 * declaration order — the helper validates parity between the per-field
 * membership declarations and this list, so the declarations remain the
 * single source of truth.
 */
function visibleColumnsFor(
  viewId: string,
  order: readonly ContactFieldId[],
): ContactFieldId[] {
  return collectFieldsForSavedView<ContactFieldId>(viewId, CONTACT_FIELDS, order);
}

// Saved views configuration
export const contactSavedViews: SavedViewConfig<ContactFieldId>[] = [
  {
    id: "all-contacts",
    name: "All Contacts",
    filters: {},
    sort: { field: "created_at", direction: "desc" as SortDirection },
    visibleColumns: visibleColumnsFor("all-contacts", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "is_active",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "active-contacts",
    name: "Active Contacts",
    filters: { is_active: true },
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("active-contacts", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "customers",
    name: "Customers",
    filters: { status: "customer" },
    sort: { field: "company_name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("customers", [
      "name",
      "email",
      "phone",
      "company_name",
      "last_contacted_at",
    ]),
  },
  {
    id: "leads",
    name: "Leads",
    filters: { status: "lead" },
    sort: { field: "created_at", direction: "desc" as SortDirection },
    visibleColumns: visibleColumnsFor("leads", [
      "name",
      "email",
      "phone",
      "company_name",
      "source",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "prospects",
    name: "Prospects",
    filters: { status: "prospect" },
    sort: { field: "created_at", direction: "desc" as SortDirection },
    visibleColumns: visibleColumnsFor("prospects", [
      "name",
      "email",
      "phone",
      "company_name",
      "source",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "contacts-with-active-deals",
    name: "Contacts With Active Deals",
    filters: { has_active_deals: true },
    sort: { field: "active_deal_count", direction: "desc" as SortDirection },
    visibleColumns: visibleColumnsFor("contacts-with-active-deals", [
      "name",
      "email",
      "phone",
      "company_name",
      "active_deal_count",
      "has_active_deals",
      "last_contacted_at",
    ]),
  },
  {
    id: "contacts-without-active-deals",
    name: "Contacts Without Active Deals",
    filters: { has_active_deals: false },
    sort: { field: "updated_at", direction: "desc" as SortDirection },
    visibleColumns: visibleColumnsFor("contacts-without-active-deals", [
      "name",
      "email",
      "phone",
      "company_name",
      "has_active_deals",
      "last_contacted_at",
    ]),
  },
  {
    id: "no-tickets-this-month",
    name: "No Tickets This Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("no-tickets-this-month", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "with-tickets-this-month",
    name: "With Tickets This Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("with-tickets-this-month", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "no-tickets-last-month",
    name: "No Tickets Last Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("no-tickets-last-month", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "with-tickets-last-month",
    name: "With Tickets Last Month",
    filters: {}, // Filters applied via custom hook in UniversalEntityPage
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("with-tickets-last-month", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
      "created_at",
    ]),
  },
  {
    id: "needs-follow-up",
    name: "Needs Follow-Up (7+ days)",
    filters: {
      filter_last_follow_up_days_ago_min: 7,
    },
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("needs-follow-up", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
      "last_follow_up_task_created_at",
    ]),
  },
  // Contract-coverage views. Backed by the contacts.has_signed_contract /
  // contract_expires_at columns mirrored down from the contact's company
  // (migration 20260818160000_crm_derived_rollups.sql).
  {
    id: "contacts-with-contract",
    name: "Company Under Contract",
    filters: { has_signed_contract: true },
    sort: { field: "contract_expires_at", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("contacts-with-contract", [
      "name",
      "email",
      "phone",
      "company_name",
      "has_signed_contract",
      "contract_expires_at",
      "last_contacted_at",
    ]),
  },
  {
    id: "contacts-without-contract",
    name: "Company Not Under Contract",
    filters: { has_signed_contract: false },
    sort: { field: "name", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("contacts-without-contract", [
      "name",
      "email",
      "phone",
      "company_name",
      "has_signed_contract",
      "last_contacted_at",
    ]),
  },
  {
    // Inverse of the last_contacted_at "within X" presets — see the
    // `not_contacted_within_days` virtual filter field.
    id: "cold-90-days",
    name: "Cold 90+ Days",
    filters: { filter_not_contacted_within_days: 90 },
    sort: { field: "last_contacted_at", direction: "asc" as SortDirection },
    visibleColumns: visibleColumnsFor("cold-90-days", [
      "name",
      "email",
      "phone",
      "company_name",
      "status",
      "last_contacted_at",
    ]),
  },
];

// Custom filter hooks for saved views that require special filtering logic
export const contactCustomFilterHooks = {
  "no-tickets-this-month": useContactsWithNoTicketsThisMonth,
  "with-tickets-this-month": useContactsWithTicketsThisMonth,
  "no-tickets-last-month": useContactsWithNoTicketsLastMonth,
  "with-tickets-last-month": useContactsWithTicketsLastMonth,
};
